import { useMemo, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/** Đầu số hay gặp ở khách của viện: Việt Nam trước, sau đó các nước châu Á rồi tới phương Tây */
export const COUNTRY_CODES = [
  { iso: 'VN', dial: '84' },
  { iso: 'US', dial: '1' },
  { iso: 'AU', dial: '61' },
  { iso: 'KR', dial: '82' },
  { iso: 'JP', dial: '81' },
  { iso: 'CN', dial: '86' },
  { iso: 'TW', dial: '886' },
  { iso: 'HK', dial: '852' },
  { iso: 'SG', dial: '65' },
  { iso: 'TH', dial: '66' },
  { iso: 'MY', dial: '60' },
  { iso: 'KH', dial: '855' },
  { iso: 'LA', dial: '856' },
  { iso: 'PH', dial: '63' },
  { iso: 'ID', dial: '62' },
  { iso: 'IN', dial: '91' },
  { iso: 'GB', dial: '44' },
  { iso: 'FR', dial: '33' },
  { iso: 'DE', dial: '49' },
  { iso: 'RU', dial: '7' },
];

export const DEFAULT_DIAL = '84';

/**
 * Ghép SĐT theo cách hệ thống đang lưu: số Việt Nam dạng "0xxxxxxxxx",
 * số nước ngoài dạng mã quốc gia + số (không có dấu +), đều bỏ số 0 đầu của số nội địa.
 */
export function toStoredPhone(dial: string, input: string): string {
  const national = input.replace(/\D/g, '').replace(/^0+/, '');
  return dial === DEFAULT_DIAL ? `0${national}` : `${dial}${national}`;
}

interface Props {
  value: string;
  onChange: (dial: string) => void;
}

/** Nút chọn đầu số quốc gia cho kiosk, mở danh sách cuộn được, bấm bằng cảm ứng. */
export default function CountryCodePicker({ value, onChange }: Props) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);

  // Tên nước lấy từ trình duyệt theo ngôn ngữ đang chọn, khỏi phải tự dịch từng nước
  const regionName = useMemo(() => {
    try {
      const names = new Intl.DisplayNames([i18n.language?.startsWith('en') ? 'en' : 'vi'], { type: 'region' });
      return (iso: string) => names.of(iso) || iso;
    } catch {
      return (iso: string) => iso;
    }
  }, [i18n.language]);

  const current = COUNTRY_CODES.find((c) => c.dial === value) || COUNTRY_CODES[0];

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        title={t('checkin.country.title')}
        className="absolute left-1.5 top-1.5 bottom-1.5 z-10 flex items-center gap-1 px-2.5 rounded-lg
                   bg-white border border-gray-200 text-spa-dark font-semibold text-sm shadow-sm hover:border-[#C79F4B] active:scale-95 transition-all"
      >
        <span className="text-[10px] text-gray-400 font-bold">{current.iso}</span>
        <span>+{current.dial}</span>
        <ChevronDown size={14} className={`text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-20" onClick={() => setOpen(false)} />
          <div
            role="listbox"
            className="absolute left-0 right-0 top-full mt-2 z-30 bg-white rounded-xl border border-gray-200 shadow-2xl overflow-hidden fade-in"
          >
            <div className="px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500 border-b border-gray-100 bg-gray-50">
              {t('checkin.country.title')}
            </div>
            <div className="max-h-[260px] overflow-y-auto py-1">
              {COUNTRY_CODES.map((c) => {
                const active = c.dial === current.dial;
                return (
                  <button
                    key={c.iso}
                    type="button"
                    role="option"
                    aria-selected={active}
                    onClick={() => {
                      onChange(c.dial);
                      setOpen(false);
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-left text-sm transition-colors ${
                      active ? 'bg-amber-50 text-spa-dark font-semibold' : 'text-gray-700 hover:bg-gray-50 active:bg-gray-100'
                    }`}
                  >
                    <span className="w-7 text-[11px] font-bold text-gray-400">{c.iso}</span>
                    <span className="flex-1 truncate">{regionName(c.iso)}</span>
                    <span className="font-semibold text-gray-500">+{c.dial}</span>
                    <Check size={16} className={active ? 'text-spa-gold' : 'invisible'} />
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </>
  );
}
